import { h } from 'vue'
import { RouterView } from 'vue-router'
import { ROUTES } from 'src/router/const'
import { devMode } from 'src/config'
import { CREATE_BANNER_STEPS } from 'src/services/consts/banner/create.const'
import { BANNER_DETAIL_TABS } from 'src/services/consts/banner/detail.const'
import { SETTINGS_TABS } from 'src/services/consts/settings.const'
import { canAccessCreateBannerStep } from 'src/services/helpers/banner/create.helper'
import { useNewBannerStore } from 'src/stores/banner/create'

const routes = [
  {
    path: '/',
    redirect: '/' + ROUTES.APP,
  },

  // Public invite landing — the invite email links here. Exempted from the
  // Auth0 guard in boot/auth0.js (meta.public) so an unauthenticated invitee
  // sees "you've been invited" copy before signing in.
  {
    path: '/' + ROUTES.ACCEPT_INVITE,
    name: ROUTES.ACCEPT_INVITE,
    meta: { public: true },
    component: () => import('src/pages/AcceptInvite.vue'),
  },

  // Public email-verification landing — Auth0's verification email redirects
  // here with its result query params. Exempted from the Auth0 guard
  // (meta.public) so the visitor reads the outcome before signing in.
  {
    path: '/' + ROUTES.EMAIL_VERIFIED,
    name: ROUTES.EMAIL_VERIFIED,
    meta: { public: true },
    component: () => import('src/pages/EmailVerified.vue'),
  },

  // Terms page — protected by global auth guard in boot/auth0.js.
  // /terms shows the latest version; /terms/:version shows a specific immutable
  // version. Unknown versions render an inline not-found (TermsContent handles it).
  {
    path: '/' + ROUTES.TERMS + '/:version?',
    name: ROUTES.TERMS,
    component: () => import('src/pages/TermsPage.vue'),
  },

  // App routes — protected by global auth guard in boot/auth0.js
  {
    path: '/' + ROUTES.APP,
    name: ROUTES.APP,
    component: () => import('src/pages/AppIndex.vue'),
    children: [
      {
        path: '',
        name: ROUTES.OVERVIEW,
        component: () => import('src/pages/OverviewIndex.vue'),
      },
      {
        path: '/' + ROUTES.BANNERS,
        component: { render: () => h(RouterView) },
        children: [
          {
            path: '',
            name: ROUTES.BANNERS,
            component: () => import('src/pages/BannersIndex.vue'),
          },
          {
            path: 'create',
            name: ROUTES.CREATE_BANNER,
            component: () => import('src/pages/CreateBannerIndex.vue'),
            meta: { headerBackRouteName: ROUTES.BANNERS },
            redirect: { name: ROUTES.CREATE_BANNER_REGULATIONS },
            children: [
              ...CREATE_BANNER_STEPS.map((step) => ({
                path: step.path,
                name: step.route,
                component: { render: () => null },
                beforeEnter: () => {
                  const store = useNewBannerStore()
                  if (!canAccessCreateBannerStep(step.key, store.bannerToEdit)) {
                    return { name: ROUTES.CREATE_BANNER_REGULATIONS }
                  }
                },
              })),
              {
                path: ':pathMatch(.*)*',
                redirect: { name: ROUTES.CREATE_BANNER_REGULATIONS },
              },
            ],
          },
          {
            path: ':siteId',
            name: ROUTES.BANNER_DETAIL,
            meta: { headerBackRouteName: ROUTES.BANNERS },
            component: () => import('src/pages/BannerDetailIndex.vue'),
            redirect: (to) => ({ name: ROUTES.BANNER_DETAIL_OVERVIEW, params: to.params }),
            children: [
              ...BANNER_DETAIL_TABS.map((tab) => ({
                path: tab.key,
                name: tab.route,
                component: { render: () => null },
              })),
              {
                path: ':pathMatch(.*)*',
                redirect: (to) => ({
                  name: ROUTES.BANNER_DETAIL_OVERVIEW,
                  params: { siteId: to.params.siteId },
                }),
              },
            ],
          },
        ],
      },
      {
        path: '/' + ROUTES.ANALYTICS,
        name: ROUTES.ANALYTICS,
        component: () => import('src/pages/AnalyticsIndex.vue'),
      },
      {
        path: '/faq/:topicId',
        name: ROUTES.FAQ_TOPIC,
        component: () => import('src/pages/faq/FaqTopicPage.vue'),
      },
      {
        path: '/faq/:topicId/:articleId',
        name: ROUTES.FAQ_ARTICLE,
        component: () => import('src/pages/faq/FaqArticlePage.vue'),
      },
      {
        path: '/faq',
        name: ROUTES.FAQ,
        component: () => import('src/pages/faq/FaqIndex.vue'),
      },
      {
        path: '/' + ROUTES.SETTINGS,
        component: () => import('src/pages/SettingsIndex.vue'),
        redirect: { name: ROUTES.SETTINGS_ACCOUNT_DETAILS },
        children: [
          {
            path: SETTINGS_TABS[ROUTES.SETTINGS_ACCOUNT_DETAILS].key,
            name: ROUTES.SETTINGS_ACCOUNT_DETAILS,
            component: () => import('src/pages/settings/SettingsAccountDetails.vue'),
          },
          {
            path: SETTINGS_TABS[ROUTES.SETTINGS_ACCOUNT_MEMBERS].key,
            name: ROUTES.SETTINGS_ACCOUNT_MEMBERS,
            component: () => import('src/pages/settings/SettingsAccountMembers.vue'),
          },
          {
            path: SETTINGS_TABS[ROUTES.SETTINGS_SYSTEM_SETTINGS].key,
            name: ROUTES.SETTINGS_SYSTEM_SETTINGS,
            component: () => import('src/pages/settings/SettingsSystemSettings.vue'),
          },
        ],
      },

      // DEV ONLY: POC pages
      ...(devMode
        ? [
            {
              path: '/' + ROUTES.ERROR_POC,
              name: ROUTES.ERROR_POC,
              component: () => import('src/pages/ErrorPOC.vue'),
            },
          ]
        : []),
    ],
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
]

export default routes
